<template>
    <!-- Messages flow in the document — the page itself scrolls, the chat
         header and composer stay sticky above/below them. -->
    <div class="@container flex-1 space-y-3 p-6">
        <template v-for="group in messageGroups" :key="group.key">
            <AiChatToolStatusGroup
                v-if="group.kind === 'tools'"
                :tools="group.tools"
            />
            <AiChatMessageBubble
                v-else
                :message="group.message"
                :chat-id="chatId"
            />
        </template>

        <div
            v-if="pending"
            class="flex items-center gap-2 text-sm text-gray-400"
        >
            <div class="robot-thinking shrink-0">
                <AnimatedRobot />
            </div>
            {{ t("vue.ai_chat_thinking") }}
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, nextTick, watch } from "vue";
import type { IAiChatMessage } from "@/types/AiChat/IAiChat";
import { groupToolStatusMessages } from "@/Pages/AiChat/messageGroups";
import { useTranslations } from "@/composables/useTranslations";

const props = withDefaults(
    defineProps<{
        messages: IAiChatMessage[];
        pending: boolean;
        chatId?: number | null;
    }>(),
    { chatId: null },
);

const { t } = useTranslations();

const messageGroups = computed(() => groupToolStatusMessages(props.messages));

const scrollToBottom = () => {
    nextTick(() => {
        window.scrollTo({ top: document.documentElement.scrollHeight });
    });
};

watch(
    () => [props.messages.length, props.pending],
    () => scrollToBottom(),
    { immediate: true },
);
</script>

<style scoped>
.robot-thinking {
    width: 34px;
    height: 34px;
    animation: thinkingFly 1.9s ease-in-out infinite;
    will-change: transform;
}

/* A busy little hover while the assistant is thinking. */
@keyframes thinkingFly {
    0%,
    100% {
        transform: translateY(0) rotate(0deg);
    }
    30% {
        transform: translateY(-5px) translateX(-3px) rotate(-6deg);
    }
    65% {
        transform: translateY(-2px) translateX(3px) rotate(6deg);
    }
}

.robot-thinking :deep(.robot-anim),
.robot-thinking :deep(.robot-anim svg) {
    width: 34px !important;
    height: 34px !important;
}

/* The shared robot shows a "Bye" speech bubble (from logout); hide it here. */
.robot-thinking :deep(#robotMessage) {
    display: none !important;
}
</style>
